import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";

const DEFAULT_TESTIMONIALS = [
  {
    name: "James Whitfield",
    role: "Golfer from Singapore",
    quote:
      "Exqura made booking our golf trip to Bali completely effortless. Every tee time, transfer, and hotel was arranged perfectly — we just showed up and played.",
    rating: "5",
    avatar: "",
  },
  {
    name: "Priya Nair",
    role: "Golfer from Kuala Lumpur",
    quote:
      "The concierge team went above and beyond, rearranging our tee times at the last minute without any hassle. Best golf travel experience we've had.",
    rating: "5",
    avatar: "",
  },
  {
    name: "David Chen",
    role: "Golfer from Hong Kong",
    quote:
      "Instant confirmation, great course selection, and unbeatable prices. Exqura has become our go-to for every golf getaway in Asia.",
    rating: "5",
    avatar: "",
  },
  {
    name: "Sarah Thompson",
    role: "Golfer from Sydney",
    quote:
      "From the ferry transfer to the resort to the fairway, everything was seamless. It genuinely felt like a five-star concierge service.",
    rating: "4",
    avatar: "",
  },
  {
    name: "Arjun Mehta",
    role: "Golfer from Mumbai",
    quote:
      "We booked a group trip for eight golfers and Exqura handled every detail. Communication was fast and the courses were exceptional.",
    rating: "5",
    avatar: "",
  },
  {
    name: "Emily Carter",
    role: "Golfer from London",
    quote:
      "Booking across time zones used to be a nightmare. Exqura's platform made planning our Southeast Asia golf tour simple and stress-free.",
    rating: "5",
    avatar: "",
  },
];

function Stars({ rating }) {
  const count = Number(rating) || 5;
  return (
    <div className="flex items-center justify-center gap-1 mb-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i < count ? "text-[#D4AF37] fill-[#D4AF37]" : "text-white/20"
          }`}
        />
      ))}
    </div>
  );
}

export default function ExquraTestimonials({ fields = {} }) {
  const {
    headline = "What Our Golfers Say",
    subtext = "Real experiences from golfers who've traveled with Exqura",
    testimonials = DEFAULT_TESTIMONIALS,
  } = fields;

  const items = testimonials?.length ? testimonials : DEFAULT_TESTIMONIALS;
  const count = items.length;
  const [current, setCurrent] = useState(0);

  const goTo = (idx) => setCurrent((idx + count) % count);
  const prev = () => goTo(current - 1);
  const next = () => goTo(current + 1);

  const t = items[current];

  return (
    <section className="bg-white py-5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#0B2421] rounded-3xl py-12 px-6 sm:px-10 lg:px-14">
          <h2 className="text-center text-[#D4AF37] font-bold text-2xl sm:text-3xl lg:text-4xl mb-3">
            {headline}
          </h2>
          {subtext && (
            <p className="text-center text-white/60 text-sm sm:text-base mb-10">
              {subtext}
            </p>
          )}

          <div className="relative max-w-2xl mx-auto">
            {/* Prev / Next arrows */}
            <button
              type="button"
              onClick={prev}
              aria-label="Previous testimonial"
              className="absolute -left-2 sm:-left-14 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/5 border border-[#D4AF37]/30 hover:bg-white/10 flex items-center justify-center text-[#D4AF37] transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next testimonial"
              className="absolute -right-2 sm:-right-14 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/5 border border-[#D4AF37]/30 hover:bg-white/10 flex items-center justify-center text-[#D4AF37] transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35 }}
                className="text-center px-6 sm:px-10"
              >
                <Quote className="w-8 h-8 text-[#D4AF37]/40 mx-auto mb-4" />
                <Stars rating={t.rating} />
                <p className="text-white/90 text-base sm:text-lg leading-relaxed mb-6 min-h-[96px] whitespace-pre-line">
                  “{t.quote}”
                </p>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center mb-2">
                    {t.avatar ? (
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-[#D4AF37] font-bold text-sm">
                        {t.name
                          ?.split(" ")
                          .map((w) => w[0])
                          .join("")
                          .slice(0, 2)}
                      </span>
                    )}
                  </div>
                  <p className="text-white font-semibold text-sm">{t.name}</p>
                  <p className="text-white/50 text-xs">{t.role}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dot indicators */}
          <div className="flex items-center justify-center gap-2 mt-10">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                className={`rounded-full transition-all duration-300 ${
                  i === current
                    ? "w-6 h-2 bg-[#D4AF37]"
                    : "w-2 h-2 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
